'use client';

import * as React from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';

interface Props {
  families: string[];
  countries: string[];
  vintages: string[];
  familyLabel: Record<string, string>;
  initialFamily: string;
  initialCountry: string;
  initialVintage: string;
  initialQuery: string;
}

export function MarketplaceFilters({
  families, countries, vintages, familyLabel,
  initialFamily, initialCountry, initialVintage, initialQuery,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [query, setQuery] = React.useState(initialQuery);

  function pushParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams?.toString() ?? '');
    if (value) params.set(key, value);
    else params.delete(key);
    router.push(`${pathname}?${params.toString()}`);
  }

  // Debounced search push
  React.useEffect(() => {
    if (query === initialQuery) return;
    const t = setTimeout(() => pushParam('q', query), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <section style={filterBarStyle}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <FilterSelect
          label="Family"
          value={initialFamily}
          onChange={(v) => pushParam('family', v)}
          options={[{ id: '', label: 'Any' }, ...families.map((f) => ({ id: f, label: `${f} ${familyLabel[f] ?? ''}` }))]}
        />
        <FilterSelect
          label="Country"
          value={initialCountry}
          onChange={(v) => pushParam('country', v)}
          options={[{ id: '', label: 'Any' }, ...countries.map((c) => ({ id: c, label: c }))]}
        />
        <FilterSelect
          label="Vintage"
          value={initialVintage}
          onChange={(v) => pushParam('vintage', v)}
          options={[{ id: '', label: 'Any' }, ...vintages.map((v) => ({ id: v, label: v }))]}
        />
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: 'var(--mcc-muted)' }}>
          <span style={{ marginBottom: 4 }}>Search project</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Project name..."
            style={{ padding: '8px 12px', border: '1px solid var(--mcc-border)', borderRadius: 6, fontSize: 14 }}
          />
        </label>
      </div>
    </section>
  );
}

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { id: string; label: string }[];
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: 'var(--mcc-muted)' }}>
      <span style={{ marginBottom: 4 }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: '8px 12px', border: '1px solid var(--mcc-border)', borderRadius: 6, fontSize: 14, background: '#fff' }}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const filterBarStyle: React.CSSProperties = {
  background: '#fff',
  borderTop: '1px solid var(--mcc-border)',
  borderBottom: '1px solid var(--mcc-border)',
  padding: '20px 24px',
};
